import InsurancesProductEnum from '@/enums/InsurancesProductEnum';
import localforage from 'localforage';
import { capitalizeFirstLetter, STEP } from './UiStepService';
import { formatNumber, formatPercent } from './ValidationService';
import { isApp, isLocalHost } from '@/views/calculators/services/environmentService';
import { exception, information, warning } from '@/appinsights/logging/ApplicationInsightLog';
import { HeaderCardBlock } from '@/cms/definitions/content-types';
import { headerCardShown } from './NavigationService';
import store from '@/store/store';
import BuyInsuranceSettings from '../BuyInsuranceSettings';
import { AdditionalInfo, CommonModel } from '../types/CommonModel';
import { almbrandOnlineCampaignName, hasDependentSplashProduct, upSaleCampaignName } from './campaignService';
import AxiosService from './axiosService';
import { defaultConfig } from '@/cms/api/ApiConfig';
import { modalWithExit, togglePopup } from './AlertService';

export const getCalculatorIndex = (name: string): number => {
	const productInx = name.replace(/[^\d]/g, '');
	return productInx === '' ? -1 : Number.parseInt(productInx);
};

export const getShortProductName = (productName: string): string => {
	if (!productName) {
		return '';
	}
	let name = stripProductIndex(productName).toLowerCase();
	const inx = getCalculatorIndex(productName);
	let productInx = '';
	if (inx > 0) {
		productInx = ' ' + (inx + 1); // næste forsikring hedder 2 ikke 1
	}
	switch (name) {
		case InsurancesProductEnum.BIL_FORSIKRING:
		case InsurancesProductEnum.INDBO_FORSIKRING:
		case InsurancesProductEnum.REJSE_FORSIKRING:
		case InsurancesProductEnum.FRITIDSHUS_FORSIKRING:
		case InsurancesProductEnum.HUS_FORSIKRING:
		case InsurancesProductEnum.BOERNE_FAMILIE_FORSIKRING:
			name = name.replaceAll('forsikring', '') + productInx;
			break;
		case InsurancesProductEnum.ULYKKES_FORSIKRING:
		case InsurancesProductEnum.BOERNEULYKKES_FORSIKRING:
			name = name.replaceAll('sforsikring', '') + productInx;
			break;
		case InsurancesProductEnum.HUNDE_FORSIKRING:
			name = name.replaceAll('eforsikring', '') + productInx;
			break;
		default:
			name = productName + productInx;
			break;
	}
	return capitalizeFirstLetter(name);
};

export const getCalculatorNameUi = (name: string): string => {
	if (!name) {
		return;
	}
	const n = name.charAt(0).toLocaleUpperCase() + name.slice(1);
	const inx = getCalculatorIndex(n);
	return inx < 1 ? n.replace('' + inx, '') : n.replace('' + inx, ' ' + (inx + 1)); // næste forsikring hedder 2 ikke 1
};

export const getCoverageNames = (selectedPackage) => {
	if (selectedPackage.expandedCoverages) {
		return selectedPackage.expandedCoverages.join(', ');
	}

	const names = [];
	selectedPackage.coverages.forEach((element) => {
		names.push(element.name ?? element.title);
	});
	return names.join(', ');
};

export const setHighLights = (model) => {
	model.overview.highlights = [];
	const isPlusCustomer = model.calculation.discount && model.calculation.discount < 1;

	let hasCampaignDiscount = false;
	let campaignDiscount = 0;
	let discountDisplay: string = undefined;
	let hasCampaignSplash = false;
	if (model.campaign?.valid) {
		hasCampaignDiscount = model.campaign.discount > 0;
		if (hasCampaignDiscount) {
			campaignDiscount = model.campaign.discount;
			discountDisplay = model.campaign.discountDisplay;
		} else {
			hasCampaignDiscount = model.campaign.tiaDiscount > 0;
			if (hasCampaignDiscount) {
				campaignDiscount = model.campaign.tiaDiscount;
				discountDisplay = formatNumber.format(100 * campaignDiscount) + ' %';
			}
		}

		// KLK SPLASH OK
		if (model.campaign.splash) {
			if (store.state.campaignSplashDependentActive) {
				hasCampaignSplash = hasDependentSplashProduct();
			} else {
				hasCampaignSplash = model.campaign.splash;
			}
		}
	}

	if (isPlusCustomer) {
		let txt = '<div class="grow"><i>Plus</i>Kunde rabat fratrukket pris </div><div>';
		if (hasCampaignDiscount) {
			txt = '<div class="grow"><i>Plus</i>Kunde rabat </div><div>';
		}
		model.overview.highlights.push(`${txt}<div>${model.calculation.discountDisplay}</div>`);
	}
	if (hasCampaignDiscount) {
		let txt = '<div class="grow">Online rabat fratrukket pris </div>';
		if (isPlusCustomer) {
			txt = '<div class="grow">Online rabat </div>';
		}
		model.overview.highlights.push(`${txt}<div>${discountDisplay}</div>`);
	}
	if (isPlusCustomer && hasCampaignDiscount) {
		const totalDiscount = Math.abs(model.calculation.discount - 1) + campaignDiscount;
		const display = formatPercent.format(totalDiscount);
		model.overview.highlights.push(
			`<div class="grow">Samlet rabat fratrukket pris </div><div><strong>${display}</strong></div>`
		);
	}

	if (hasCampaignSplash) {
		model.overview.highlights.push(
			`<div class="grow">Kampagne </div><div>${model.campaign.splash.displayText}</div>`
		);
	}
};

export const setGlobalPackageSums = (model) => {
	if (model.calculation.abCalc.globalSums && model.overview?.details) {
		model.calculation.abCalc.globalSums.forEach((sum) => {
			model.overview.details.push(sum.key + ': ' + sum.value);
		});
	}
};

export const setDetailsYearlyPrice = (model, cms) => {
	if (!cms.overviewShowYearlyPrice) {
		// wait for all calcs
		let max = 20;
		const interval = setInterval(() => {
			max--;
			if (model.choosePackage.selectedPackage.yearlyPriceTotalDisplay || max < 1) {
				model.overview.details = [
					'Årlig pris: ' + model.choosePackage.selectedPackage.yearlyPriceTotalDisplay,
					...model.overview.details,
				];
				clearInterval(interval);
			}
		}, 500);
	}
};

export const getCardTitle = (card, model, cms): string => {
	return card.name === STEP.PACKAGE && model.choosePackage.selectedPackage !== undefined
		? cms.calculatedHeadline
		: card.title;
};

export const getSubtitle = (cardName: string, model) => {
	switch (cardName) {
		case STEP.CAR_INFO:
			return model.carInfo.subtitle;
		case STEP.DOG_INFO:
			return model.dogInfo.subtitle;
		case STEP.CHILDREN_INFO:
			return model.familyInfo.subtitle;
		case STEP.PERSON_INFO:
			return model.personInfo.subtitle;
		case STEP.OVERVIEW:
			return model.overview.subtitle;
		case STEP.ADDITIONAL_INFO:
			return model.additionalInfo.subtitle;
		case STEP.PACKAGE:
			return model.choosePackage.subtitle;
	}
	return model[cardName] ? model[cardName].subtitle : undefined;
};

export const stripProductIndex = (productName: string) => {
	return productName.replace(/\d/g, ''); // remove indexes (bilforsikring0 -> bilforsikring, bilforsikring1 -> bilforsikring)
};

export const getUrlProductName = (productName) => {
	const name = stripProductIndex(productName);

	switch (name) {
		case InsurancesProductEnum.BOERNEULYKKES_FORSIKRING:
			return 'boerneulykkesforsikring';
		case InsurancesProductEnum.BOERNE_FAMILIE_FORSIKRING:
			return 'boernefamilieforsikring';
		default:
			return name;
	}
};

// cleanup campaign in storage if it exists
export const resetCampaignStorage = (productName: string) => {
	try {
		const campaignName = 'campaign' + productName;
		localforage.removeItem(campaignName);
	} catch (e) {
		// no problem
	}
};

// cleanup all campaigns in storage if they exists
export const resetCampaignStorageAll = (products: string[]) => {
	products.forEach((product) => {
		resetCampaignStorage(product);
	});
};

//update excesses from calculation result
export const updateExcessesFromCalculation = (response: any, calcConfig: any): boolean => {
	try {
		if (isLocalHost) {
			information('updateExcessesFromCalculation');
		}
		const excesses: Array<{ id: number; amount: number }> = response?.data?._embedded?.excesses;
		if (excesses?.length > 0) {
			excesses.forEach((excess) => {
				if (Number.isNaN(excess.amount)) {
					warning('update excess isNAN', excess);
					return;
				}

				calcConfig.excessList.forEach((excessCurrent, inx) => {
					// car excess Ids are strings!
					if (excess.id + '' === excessCurrent.id + '') {
						if (isLocalHost) {
							information('updating', {
								excessCurrentId: excessCurrent.id,
								excessCurrentAmount: excessCurrent.amount,
							});
						}
						if (isLocalHost || excess.amount !== calcConfig.excessList[inx].amount) {
							warning('update excess', { excess, excessList: calcConfig.excessList[inx] });
							calcConfig.excessList[inx].amount = excess.amount;
						}

						if (isLocalHost) {
							information('updated', {
								excessId: calcConfig.excessList[inx].id,
								excessAmount: calcConfig.excessList[inx].amount,
							});
						}
					}
				});
			});
			return true;
		}
	} catch (e) {
		exception(e);
		// Too bad
	}
	return false;
};

export const addHeaderCardBlock = (headerBlock: HeaderCardBlock, uiComp) => {
	const text = headerBlock.text && headerBlock?.text?.trim() === '' ? '' : headerBlock.text;
	const hasCss = headerBlock.css && headerBlock.css?.trim() !== '';

	const isHeaderCardShown = headerCardShown(headerBlock.contentId);
	if (!isHeaderCardShown) {
		addHeaderParts(headerBlock, text, hasCss, uiComp);
	}
};

const addHeaderParts = (headerBlock: HeaderCardBlock, text, hasCss, uiComp) => {
	let headerCardTxt = '';
	if (text !== '') {
		headerCardTxt = text;
	}
	if (hasCss) {
		headerCardTxt += headerBlock.css;
	}

	const campaignId = headerBlock.id?.toLowerCase() ?? headerBlock.contentName.toLowerCase();

	uiComp.headerCards.set(campaignId, headerCardTxt);

	if (almbrandOnlineCampaignName === campaignId) {
		// also add upsale system campaign
		uiComp.headerCards.set(upSaleCampaignName, headerCardTxt);
	}

	// if (headerBlock.script) {
	// 	const s = document.createElement('script');
	// 	s.type = 'text/javascript';
	// 	try {
	// 		s.appendChild(document.createTextNode(headerBlock.script));
	// 		document.body.appendChild(s);
	// 	} catch (e) {
	// 		exception(e);
	// 	}
	// }
};
// filter our multiple entries in array
export const uniqueStringArray = (array: string[]): string[] => {
	return [...new Set(array)] as string[];
};

// Get consent text and map brand
export const initConsent = async (cms: BuyInsuranceSettings) => {
	if (!store.state.consent.text) {
		// CMS fallBack for acceptOtherCompanyConsent
		store.state.consent = {
			originalText: cms.consentText,
			text: cms.consentText,
			originalDetailsText: cms.consentDetailsText === '' ? undefined : cms.consentDetailsText,
			detailsText: cms.consentDetailsText === '' ? undefined : cms.consentDetailsText,
			linkText: cms.consentLinkText === '' ? undefined : cms.consentLinkText,
		};

		// if (cms.useConsentTermination) {
		// Consent other companies
		try {
			const axiosService = new AxiosService();
			const res = await axiosService.get(
				defaultConfig.baseUrl +
					cms.openServiceConsentUrl +
					// DEBUG '/consents/consentlist?BusinessBrand=ALMBRAND'
					'/consents/consentlist'
			);
			if (res?.status === 200) {
				if (res.data?.length > 0) {
					res.data.forEach((consent) => {
						// DEBUG if (consent.businessBrand === 'ALMBRAND' && consent.businessName === 'Promotion Digital') {
						if (consent.developerName === 'onlinetermination_private' && consent.consentText?.length > 0) {
							// DEBUG consent.consentText = cms.consentDetailsText;
							store.state.consent.originalDetailsText = consent.consentText;
						}
					});
				}
			}
		} catch (error) {
			exception(error);
		}
		// }
	}
};

export const addCompanyToConsent = (cms: BuyInsuranceSettings, model: CommonModel): void => {
	let replaceTxtCompanies = cms.consentTextNoCompany;
	if (model.productName == 'kurv') {
		const companies = getFormerInsuranceCompanies();
		if (companies.length > 0) {
			replaceTxtCompanies = '';
			companies.forEach((company) => {
				replaceTxtCompanies += `<li>${company}</li>`;
			});

			// DEBUG replaceTxtCompanies = companies.join(', ');
			// DEBUG replaceTxtCompanies = replaceLastOccurrence(replaceTxtCompanies, ', ', ' og ');
		}
	} else {
		const companies = getFormerInsuranceCompany(model);
		if (companies.length > 0) {
			replaceTxtCompanies = '';
			companies.forEach((company) => {
				replaceTxtCompanies += `<li>${company}</li>`;
			});
		}
	}

	replaceTxtCompanies = `<ul>${replaceTxtCompanies}</ul>`;

	store.state.consent.text = store.state.consent.originalText.replaceAll(
		'[SELSKAB Forsikring]',
		// DEBUG '<span style="color: red;"><li>[SELSKAB Forsikring - DYNAMISK VALG]</span>' +
		replaceTxtCompanies
	);

	store.state.consent.text = store.state.consent.text.replaceAll(
		'[BRAND]',
		// DEBUG '<span style="color: red;">[BRAND - DYNAMISK VALG]</span>Alm. Brand forsikring A/S'
		'Alm. Brand forsikring A/S'
	);

	if (store.state.consent.originalDetailsText) {
		store.state.consent.detailsText = store.state.consent.originalDetailsText.replaceAll(
			'[BRAND]',
			// DEBUG '<span style="color: red;">[BRAND - DYNAMISK VALG]</span>Alm. Brand forsikring A/S'
			'Alm. Brand forsikring A/S'
		);

		store.state.consent.detailsText = store.state.consent.detailsText.replaceAll(
			'[SELSKAB Forsikring]',
			// DEBUG '<span style="color: red;">[SELSKAB Forsikring - DYNAMISK VALG]</span>' +
			replaceTxtCompanies
		);
		store.state.consent.detailsText = store.state.consent.detailsText.replaceAll(
			'[MAIL@BRAND.dk]',
			// DEBUG '<span style="color: red;">[mail@BRAND.dk DYNAMISK VALG]</span><a href="mailto:samtykke@almbrand.dk" target="_blank">samtykke@almbrand.dk</a>'
			'<a href="mailto:samtykke@almbrand.dk" target="_blank">samtykke@almbrand.dk</a>'
		);
		store.state.consent.detailsText = store.state.consent.detailsText.replaceAll(
			'[LINK].',
			// DEBUG '<span style="color: red;">[LINK DYNAMISK VALG].</span><a href="https://www.almbrand.dk/kundeservice/generel-information/privatlivspolitik/" target="_blank"><span>her<span class="external-link"><svg class="external-link__icon external-link__icon__x-small" version="1.1" id="Lag_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 24 24" fill="currentColor" xml:space="preserve"><polygon points="22,2 13,2 13,4 18.6,4 11.3,11.3 12.7,12.7 20,5.4 20,11 22,11"></polygon><polygon points="19,20 4,20 4,5 10,5 10,3 2,3 2,3 2,22 2,22 21,22 21,14 19,14"></polygon></svg></span></span></a>'
			'<a href="https://www.almbrand.dk/kundeservice/generel-information/privatlivspolitik/" target="_blank"><span>her<span class="external-link"><svg class="external-link__icon external-link__icon__x-small" version="1.1" id="Lag_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 24 24" fill="currentColor" xml:space="preserve"><polygon points="22,2 13,2 13,4 18.6,4 11.3,11.3 12.7,12.7 20,5.4 20,11 22,11"></polygon><polygon points="19,20 4,20 4,5 10,5 10,3 2,3 2,3 2,22 2,22 21,22 21,14 19,14"></polygon></svg></span></span></a>'
		);
	}
};

const getFormerInsuranceCompany = (model: CommonModel): string[] => {
	const additionalInfo: AdditionalInfo = model?.additionalInfo;
	// DEBUG console.log('additionalInfo', additionalInfo);

	const formerInsuranceCompanies: string[] = [];
	if (additionalInfo?.existInsurance === 'ja') {
		formerInsuranceCompanies.push(additionalInfo.existingInsurance);
	} else if (additionalInfo?.existFormerInsurance === 'ja') {
		formerInsuranceCompanies.push(additionalInfo.formerInsurance);
	}

	// Children
	const familyInfo = (model as any)?.familyInfo;
	if (familyInfo) {
		for (let i = 0; i < 6; i++) {
			if (familyInfo['existInsurance' + i] === 'ja') {
				formerInsuranceCompanies.push(familyInfo['existingInsurance' + i]);
			}
		}
	}

	// accidents
	// DEBUG console.log('formerInsuranceCompanies', formerInsuranceCompanies);

	return formerInsuranceCompanies;
};

export const getFormerInsuranceCompanies = (): string[] => {
	const basket = store.getters.getSelectableGroup('basket');
	let companies = [];
	basket.forEach((productId: string) => {
		const product = store.getters.getCalculator(productId);
		if (product?.model) {
			const companies_ = getFormerInsuranceCompany(product.model);
			if (companies_.length > 0) {
				companies.push(...companies_);
			}
		}
	});
	companies = uniqueStringArray(companies);
	return companies;
};

export const getFormerInsuranceCompanyIds = (companies: string[], cms: BuyInsuranceSettings): string[] => {
	const companyIds = [];
	companies.forEach((company: string) => {
		const inx = cms.insuranceCompanies.indexOf(company);
		companyIds.push(cms.insuranceCompaniesIds[inx]);
	});

	return companyIds;
};

// DEBUG const replaceLastOccurrence = (text: string, replace: string, replaceWith: string): string => {
// DEBUG 	try {
// DEBUG 		const startInx = text.lastIndexOf(replace);
// DEBUG 		if (startInx > -1) {
// DEBUG 			return text.substring(0, startInx) + replaceWith + text.substring(startInx + replace.length);
// DEBUG 		}
// DEBUG 	} catch (error) {
// DEBUG 		// Too bad
// DEBUG 	}
// DEBUG 	return text;
// DEBUG };

export const hasProductExistingInsurance = (model: CommonModel): boolean => {
	if (model?.additionalInfo) {
		// check existing company and former
		let hasFormerInsuranceCompany = false;
		if (model.additionalInfo.existInsurance === 'ja') {
			hasFormerInsuranceCompany = !!model.additionalInfo.existingInsurance;
		} else if (model.additionalInfo.existFormerInsurance === 'ja') {
			hasFormerInsuranceCompany = !!model.additionalInfo.formerInsurance;
		}
		// Children
		const familyInfo = (model as any)?.familyInfo;

		if (!hasFormerInsuranceCompany && familyInfo) {
			for (let i = 0; i < 6; i++) {
				if (familyInfo['existInsurance' + i] === 'ja') {
					hasFormerInsuranceCompany = !!familyInfo['existingInsurance' + i];
					// existingInsurance5: undefined,
					// existingInsuranceId5: undefined,
				}
			}
		}

		return hasFormerInsuranceCompany;
	}
	return false;
};

export const isCalculatingExistingInsurance = (
	productType: 'travel' | 'home' | 'car',
	productName: 'rejseforsikring' | 'indboforsikring' | 'bilforsikring',
	cms: BuyInsuranceSettings,
	model: CommonModel
): boolean => {
	if (!cms.hasExistingInsuranceCheck) {
		return false;
	}
	let isCalculatingExistingInsurance = false;
	let productId = undefined;
	if (store.state.customerProducts?.policies?.length > 0) {
		store.state.customerProducts.policies.forEach((product) => {
			if (product?.type?.toLowerCase()?.trim() === productType) {
				if (product.status?.type?.toUpperCase()?.trim() === 'ACTIVE') {
					isCalculatingExistingInsurance = true;
					productId = product.id;
				}
			}
		});
	}

	if (isCalculatingExistingInsurance) {
		const title = cms.hasExistingInsuranceCalculationTitle;
		const content = cms.hasExistingInsuranceCalculationContent;
		existingAlmbrandInsuranceModal(title, content, cms, model, productId);
	}
	return isCalculatingExistingInsurance;
};

export const existingAlmbrandInsuranceModal = (
	title: string,
	content: string,
	cms: BuyInsuranceSettings,
	model: CommonModel,
	productId?: string
) => {
	const modal = modalWithExit(cms);
	const loggedIn = store.state.customer !== undefined;

	modal.title = title;
	modal.content = content;
	modal.track = true;
	modal.trackToken = `calculate own ${model.productName}`;
	modal.productName = model.productName;
	modal.stepName = loggedIn ? 'person_info' : 'additional_info';
	modal.errorType = 'info';
	modal.id = `calcWarningExistingInsurance_${model.productName}`;
	modal.btnInlineLabel = loggedIn ? `Se min ${model.productName}` : 'Mit Alm. Brand';
	modal.btnInlineAction = () => {
		if (!loggedIn) {
			location.href = 'https://mit.almbrand.dk';
			return;
		}
		let url = cms.hasExistingInsuranceUrl + '?id=' + productId;
		if (isApp()) {
			url += '&client=app';
		}
		location.href = url;
	};
	togglePopup(cms, modal);
};
